import decodeToken from "./decodeToken";

//ss// activeContext — THE single source of truth for "whose account am I in".
//
// Before this module there were three, and they disagreed:
//   1. localStorage.teamOwnerId          — one owner, written once at login
//   2. sessionStorage.SelectAgentOwnerId — written when an agent is opened
//   3. teamStore.teams                   — the real multi-owner list
// plus two permission hooks resolving against different ones of those. Adding an
// access picker on top of that would have made a fourth. Everything now goes
// through here.
//
// `teamOwnerId` is still WRITTEN (never read directly outside this file) because
// ~23 components already read it. Keeping it as a mirror means those components
// become correct on a context switch for free, instead of needing 23 edits.
//
// The context is authoritative on the SERVER, in the JWT. This module only
// mirrors it into storage for synchronous reads during render.

const LS_OWNER = "teamOwnerId";           // legacy mirror — keep writing it
const SS_AGENT_OWNER = "SelectAgentOwnerId";
const SS_AGENT = "SelectAgentId";
const SS_AGENT_BUSINESS = "SelectAgentBusinessId";

/** The account the session is currently acting in. Never null when signed in. */
export function getActiveOwnerId() {
  const decoded = decodeToken(localStorage.getItem("token") || "");
  // The token wins: it is what the backend enforces against. Storage is only a
  // fallback for the moment between a switch and the reload.
  return decoded?.ownerId || localStorage.getItem(LS_OWNER) || decoded?.id || null;
}

/**
 * The owner of the agent that is currently open, else the active context.
 *
 * Legacy sessions (tokens minted before the access picker) still show every
 * company's agents at once, so "the active context" can be a different company
 * from the agent on screen. Pounce screens send this as X-Owner-Id and resolve
 * permissions against it, exactly as the receptionist screens pass the agent's
 * owner explicitly. New sessions: both values are the same.
 */
export function getAgentOwnerId() {
  return sessionStorage.getItem(SS_AGENT_OWNER) || getActiveOwnerId();
}

/** The signed-in person's OWN account id (never changes while signed in). */
export function getSelfId() {
  return decodeToken(localStorage.getItem("token") || "")?.id || null;
}

/** True when acting inside the user's own workspace rather than someone else's. */
export function isOwnContext() {
  const self = getSelfId();
  const active = getActiveOwnerId();
  return !!self && (!active || active === self);
}

/** Role in the CURRENT context: "owner" | "admin" | "support". */
export function getActiveRole() {
  if (isOwnContext()) return "owner";
  return decodeToken(localStorage.getItem("token") || "")?.role || "support";
}

/**
 * How many contexts this session can choose between (own account + teams).
 * Stamped into the token at login so the picker can be skipped without a fetch.
 */
export function getContextCount() {
  const decoded = decodeToken(localStorage.getItem("token") || "");
  return Number(decoded?.contextCount || 1);
}

/**
 * Must this session choose an access context before it can be routed anywhere?
 *
 * True only when the person can act in more than one account AND has not yet
 * made an explicit choice. `ctxPicked` is stamped exclusively by
 * POST /team/switch-context, so it means "the user actually chose" rather than
 * "a context happens to be set".
 *
 * Every post-auth redirect MUST consult this. There is more than one: the login
 * screens, resolveHomeRoute (return visits), and App's /signup route guard —
 * that last one fires the moment a token lands in localStorage and re-renders
 * App, which is how it used to win the race against the login screen's own
 * navigate() and drop the user on /fast-agent-detail before the picker showed.
 *
 * @param {object} decoded  a decoded JWT; omit to read the current token.
 */
export function needsAccessPicker(decoded) {
  const d =
    decoded || decodeToken(localStorage.getItem("token") || "") || {};
  return (!!d?.isTeamMember || Number(d?.contextCount || 0) > 1) && !d?.ctxPicked;
}

/**
 * Apply a context locally after the server has re-signed the token.
 *
 * Anything derived from the OLD context has to die here, or the new context
 * renders with the previous account's data: the selected agent (a different
 * company's agent id), any cached dashboard, and the axios owner header.
 *
 * @param {string} token     the re-signed JWT from POST /api/team/switch-context
 * @param {string} ownerId   the account now being acted in
 * @param {object} [deps]    { setContactOwnerId } — injected to avoid importing
 *                           apiStore here (apiStore imports this module).
 */
export function applyContext(token, ownerId, deps = {}) {
  localStorage.setItem("token", token);

  const selfId = decodeToken(token)?.id || null;
  if (ownerId && ownerId !== selfId) {
    localStorage.setItem(LS_OWNER, ownerId);
  } else {
    // Own account → clear the team hint so every legacy `!!teamOwnerId` check
    // correctly reports "not in a team context".
    localStorage.removeItem(LS_OWNER);
  }

  // Stale per-agent selection from the previous context.
  sessionStorage.removeItem(SS_AGENT_OWNER);
  sessionStorage.removeItem(SS_AGENT);
  sessionStorage.removeItem(SS_AGENT_BUSINESS);
  sessionStorage.removeItem("SelectAgentBusinessName");
  localStorage.removeItem("selectedAgentAvatar");

  if (typeof deps.setContactOwnerId === "function") {
    deps.setContactOwnerId(ownerId && ownerId !== selfId ? ownerId : null);
  }
}

/**
 * Re-apply the stored context to the axios layer on a cold page load.
 * Called once from App so a refresh keeps sending the right owner header.
 */
export function primeContext(setContactOwnerId) {
  const selfId = getSelfId();
  const active = getActiveOwnerId();
  if (typeof setContactOwnerId === "function") {
    setContactOwnerId(active && active !== selfId ? active : null);
  }
}
